// Editorial product recommendations. No real affiliate links or invented prices —
// price ranges are indicative buckets, and components are structured so real
// affiliate links can be dropped in later. Presentation is editorial, not e-commerce.

export const PRODUCTS = {
  "outdoor-fan": {
    name: "Outdoor Oscillating Fan",
    category: "Backyard mosquito control",
    badge: "DuckTheBugs Pick",
    priceRange: "$",
    whatWeLike: ["Mosquitoes are weak flyers — moving air genuinely keeps them off you", "Zero chemicals, works instantly, reusable every season", "Doubles as patio comfort on still summer evenings"],
    whatToKnow: ["Only protects the area the air actually reaches", "Look for a weather-rated model if it lives outside"],
    kidsPetsNote: "No chemical exposure at all — a great first move for families.",
  },
  "larvicide-dunks": {
    name: "Larval-Control Dunks (for water you keep)",
    category: "Standing water",
    badge: "Best for problem water",
    priceRange: "$",
    whatWeLike: ["Targets larvae in water you can't simply dump (rain barrels, ornamental water)", "A little goes a long way"],
    whatToKnow: ["Follow the product label exactly for dosing and any pet/wildlife guidance", "Dumping water you don't need is still the better first step"],
    kidsPetsNote: "Read the label for guidance on water features pets may drink from.",
  },
  "repellent": {
    name: "EPA-Registered Personal Repellent",
    category: "Protective basics",
    badge: "Everyday essential",
    priceRange: "$",
    whatWeLike: ["Reliable protection during dawn/dusk peak hours", "Works for both mosquitoes and ticks when you're near the edge"],
    whatToKnow: ["Choose an EPA-registered product and apply per its label", "Different formulas suit different ages — check the label for children"],
    kidsPetsNote: "Follow label age guidance; some formulas specify minimum ages.",
  },
  "gutter-guard": {
    name: "Gutter Guards",
    category: "Prevention",
    badge: "Best long-term fix",
    priceRange: "$$",
    whatWeLike: ["Stops the clogged-gutter puddles that quietly breed mosquitoes", "Set-and-forget once installed"],
    whatToKnow: ["Upfront effort to install; pays off over seasons"],
    kidsPetsNote: "Purely structural — no exposure considerations.",
  },
  "tick-tubes": {
    name: "Tick Tubes (edge treatment)",
    category: "Tick prevention",
    badge: "Best budget option",
    priceRange: "$",
    whatWeLike: ["Targets ticks at the source along the yard edge", "Simple seasonal placement"],
    whatToKnow: ["Placement timing matters — follow the product guidance", "Works best combined with edge maintenance"],
    kidsPetsNote: "Place per label; keep out of children's reach.",
  },
  "yard-barrier": {
    name: "Wood-Chip / Gravel Edge Barrier",
    category: "Tick prevention",
    badge: "DuckTheBugs Pick",
    priceRange: "$$",
    whatWeLike: ["A dry barrier ticks are reluctant to cross", "No chemicals — pure landscaping"],
    whatToKnow: ["A weekend project depending on yard size", "Maintain the strip so it stays clear"],
    kidsPetsNote: "Chemical-free — ideal for households weighing exposure.",
  },
  "pet-tick-prevention": {
    name: "Veterinary Flea & Tick Prevention",
    category: "Pets",
    badge: "Vet-directed",
    priceRange: "$$",
    whatWeLike: ["The single most important line of defense for pets", "Protects the animal that brings pests into the home"],
    whatToKnow: ["Always get the right product from your veterinarian for your specific pet", "Never use a product meant for one species on another"],
    kidsPetsNote: "This one is entirely a vet conversation — please don't improvise.",
  },
  "wasp-spray": {
    name: "Long-Range Wasp & Hornet Spray",
    category: "Traps & tools",
    badge: "Only for small exposed nests",
    priceRange: "$",
    whatWeLike: ["Distance lets you treat a small exposed nest from a safe standoff"],
    whatToKnow: ["Only for small, new, exposed, low nests — never voids, heights, or doorways", "Follow the label precisely, including protective clothing"],
    kidsPetsNote: "Keep everyone well clear; anything near play areas is a pro job.",
  },
  "protective-clothing": {
    name: "Protective Clothing & Gloves",
    category: "Protective clothing",
    badge: "Safety basics",
    priceRange: "$",
    whatWeLike: ["Reduces contact for edge work, wasp situations, and yard maintenance"],
    whatToKnow: ["Not a substitute for calling a pro on high-risk jobs"],
    kidsPetsNote: "No exposure considerations.",
  },
  "ant-bait": {
    name: "Ant Bait Stations",
    category: "Traps & tools",
    badge: "DuckTheBugs Pick",
    priceRange: "$",
    whatWeLike: ["Workers carry the product back to the colony — more effective than surface spray", "Discreet and easy to place along trails"],
    whatToKnow: ["Follow the label for placement and quantity", "Give it time — colony control isn't instant"],
    kidsPetsNote: "Place out of reach of children and pets per the label.",
  },
  "caulk-sealant": {
    name: "Caulk & Gap Sealant",
    category: "Screens & barriers",
    badge: "Best prevention",
    priceRange: "$",
    whatWeLike: ["Closes the entry points ants and other insects use", "Cheap, and it fixes the underlying issue"],
    whatToKnow: ["Find the actual entry points first for best results"],
    kidsPetsNote: "Purely structural — no exposure.",
  },
  "flea-vacuum": {
    name: "A Genuinely Good Vacuum",
    category: "Traps & tools",
    badge: "Underrated essential",
    priceRange: "$$",
    whatWeLike: ["Removes flea eggs and larvae — not just adults — from carpets and cracks", "The chemical-free backbone of any flea plan"],
    whatToKnow: ["Empty/dispose of contents so eggs don't survive indoors", "Consistency matters more than any single deep clean"],
    kidsPetsNote: "Chemical-free and safe to run daily during an infestation.",
  },
  "steel-wool-sealant": {
    name: "Gnaw-Resistant Sealing Kit",
    category: "Screens & barriers",
    badge: "Exclusion essential",
    priceRange: "$",
    whatWeLike: ["Seals the small gaps rodents exploit", "Exclusion is what actually prevents a repeat"],
    whatToKnow: ["You have to find every gap — small openings are enough", "Pair with removing food access"],
    kidsPetsNote: "Structural — keep loose materials away from pets during install.",
  },
  "snap-traps": {
    name: "Traditional Snap Traps",
    category: "Traps & tools",
    badge: "For minor, early activity",
    priceRange: "$",
    whatWeLike: ["Effective for a single mouse caught early", "No poison in the home"],
    whatToKnow: ["Ongoing activity means it's time for a pro", "Placement and safety matter"],
    kidsPetsNote: "Place well out of reach of children and pets.",
  },
};

export const DIY_CATEGORIES = [
  { slug: "prevention", name: "Prevention", icon: "ShieldCheck", body: "The cheapest pest control is the problem you never create. Prevention is where every DIY plan should start." },
  { slug: "backyard-mosquito", name: "Backyard Mosquito Control", icon: "Droplets", body: "Dump standing water, improve drainage, cut back shade, and move the air. Most nuisance mosquito problems start here." },
  { slug: "tick-prevention", name: "Tick Prevention", icon: "Bug", body: "Manage the yard edge, keep grass short, move play areas to the sun, and build a tick-check habit." },
  { slug: "standing-water", name: "Standing Water", icon: "Waves", body: "The number-one mosquito breeding source — and the easiest thing to fix on a weekly walk." },
  { slug: "yard-maintenance", name: "Yard Maintenance", icon: "Trees", body: "Mowing, trimming, and clearing debris removes the humid resting and nesting spots pests rely on." },
  { slug: "traps-tools", name: "Traps & Tools", icon: "Wrench", body: "Baits, traps, and the occasional targeted product — used correctly and per the label." },
  { slug: "protective-clothing", name: "Protective Clothing", icon: "Shirt", body: "Simple gear that reduces contact for edge work, wasps, and yard chores." },
  { slug: "screens-barriers", name: "Screens & Barriers", icon: "SquareStack", body: "Sealing gaps and repairing screens keeps pests out of the house in the first place." },
  { slug: "outdoor-fans", name: "Outdoor Fans", icon: "Fan", body: "An underrated, chemical-free way to keep mosquitoes off a patio or seating area." },
  { slug: "pest-identification", name: "Pest Identification", icon: "Search", body: "Knowing what you're dealing with is half the battle — and decides whether it's DIY or pro." },
];
